import { DecisionType } from "@/common/decisions";
import { updateUserPrefEvent, UserPrefs } from "@/stores/userPrefsDomain";
import { compact, debounce, groupBy, identity, orderBy, uniqBy } from "lodash";
import {
  addCompaniesToPortfolio,
  closeCompanyDialog,
  closeMoveToPortfolioDialog,
  closeProjectDialog,
  closeRemoveFromPortfolioDialog,
  companiesInPortfolio,
  CompanySearchDialogState,
  ConnectSubsidiaryItemState,
  createBatchProject,
  dissolvePortfolio,
  extractUserCompaniesInPortfolio,
  filterPortfolioWithText,
  InitPortfolioCompaniesPageProps,
  isSamePortfolio,
  mapRoleToPortfolioType,
  mapToHeader,
  moveCompanyToPortfolio,
  navigateToCompaniesTabAction,
  navigateToPortfolioCompaniesAction,
  newProjectDialog,
  NotConnectedTabState,
  openCompanyDialog,
  openExportShareholdersDialog,
  openSignShareholderRegistersDialog,
  closeSignShareholderRegistersDialog,
  createShareholderRegisterSigningProject,
  SignShareholderRegistersForm,
  openInviteAdminDialog,
  openMoveToPortfolioDialog,
  openRemoveFromPortfolioDialog,
  openSendAdministratorInvitationDialog,
  openTransferOwnershipDialog,
  portfolioApi,
  PortfolioCompaniesTab,
  portfolioCreationInfo,
  PortfolioPageDispatcher,
  PortfolioPageState,
  PortfolioPageStore,
  portfolioInstance,
  PortfolioAllCompanies,
  portfolioMyInvestments,
  PortfolioResponse,
  PortfolioSettingsActions,
  portfolioWithAdminRole,
  portfolioWithBoardRole,
  PreparedPageState,
  ReadyState,
  removeFromPortfolio,
  savePortfolioSettings,
  searchForCompany,
  searchSwedishBusinessRegister,
  selectCompany,
  selectCompanyFromSearchResult,
  SelectedPortfolioOption,
  selectSwedishCompany,
  sendAskForPermissionRequest,
  setDestinationPortfolio,
  subsidiariesToPortfolioCompany,
  toFollowUpCounts,
  toCompaniesWithDbotAccess,
  toggleCompanyInAddDialog,
  toggleConfirmDissolvePortfolio,
  toggleDecision,
  toggleNorwegianCompanySearch,
  togglePortfolioSettings,
  toggleSwedishCompanySearch,
  toSelectableCompanies,
  updateStore,
  PortfolioType,
} from "@/portfolio/portfolioDomain";
import { Language } from "@/i18n/languages";
import { AccessType } from "@/types/enums/AccessType";
import { MeetingType } from "@/meetings/meetingsDomain";
import {
  CompanyState,
  InvitationResponse,
  PortfolioCompany,
  ShareholderRegisterStatementPortfolioStatusType,
  shareholderRegisterStatementPortfolioStatusTypeLabels,
} from "@/stores/PortfolioStore";
import { SuggestedCompany } from "@/types/CompanyInfo";
import { createProjectName } from "@/dbot/projectList/projectListDomain";
import {
  DCEvent,
  UpdateStoreEvent,
  copyToClipboard,
  navigate,
  showAlert,
  showCreatePortfolioDialog,
  showError,
  showGeneralError,
  showGeneralSuccess,
} from "@/events/eventsDomain";
import {
  adminBeneficialOwners,
  adminProjects,
  adminShareholderStatement,
  adminShareholdersTransaction,
} from "@/utils/navigation";
import { StarredCompany } from "@/types/UserInfo";
import { orderlessFilter } from "@/utils/filter";
import {
  CompanyItem,
  emailValidator,
  failure,
  isOk,
  ok,
  pending,
  Portfolio,
} from "@/common/common";
import { TMessageKey } from "@/i18n/types.gen";
import { ApiService, HttpStatus, isHttpError } from "@/api";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { TabButtonProps } from "@/components-v2/Tabs";
import { SelectProps } from "@/components-v2/Select";
import {
  getRoleNameByLanguage,
  InviteUserToCustomRoleModel,
} from "@/portfolio/contacts/customRoleDomain";
import { CustomRole, CustomRoleItem } from "@/common/customRole";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { QuickFilter, QuickFilterBarProps } from "@/components-v2/QuickFilterBar";
import { TMessage } from "@/i18n/tt.ts";

export type PortfolioCompanyGroup = {
  portfolioName: string;
  portfolioId: string;
  companies: PortfolioCompany[];
};

export type PortfolioCompaniesActions = {
  isCompanySelected: (orgNo: string) => boolean;
  isCompanyDisabled?: (orgNo: string) => boolean;
  toggleCompanyInAddDialog: (orgNo: string) => void;
  toggleAllCompanies: () => void;
  deselectAllCompanies: () => void;
  filterSelectedCompanies?: () => void;
};

export type PortfolioCompaniesSelectProps = {
  companies: PortfolioCompany[];
  companySearchFieldModel: StringFieldModel;
  highlightWords?: string[];
  showSelectedAll?: boolean;
  isContextCustomRole?: boolean;
  selectedCompaniesCount: number | string;
  action?: PortfolioCompaniesActions;
};
export const prepareState = ({
  store,
  dispatch,
  i18n,
  portfolio,
  onRemovePortfolio,
  onUpdatePortfolio,
  locale,
  setCustomRoleId,
  apiService,
}: {
  store: PortfolioPageStore;
  dispatch: PortfolioPageDispatcher;
  i18n: PreppedI18nState;
  portfolio: SelectedPortfolioOption;
  onRemovePortfolio: (portfolioId: string) => void;
  onUpdatePortfolio: (portfolio: Portfolio) => void;
  locale: Language;
  setCustomRoleId?: (roleId: string) => void;
  apiService: ApiService;
}): PreparedPageState => {
  const isSystemPortfolio = portfolio.type !== "portfolio-instance";
  const portfolioState: PortfolioPageState = store.portfolioState ?? {
    type: "loading-portfolio",
    portfolio,
  };
  switch (portfolioState.type) {
    case "invalid-portfolioid":
      return { state: portfolioState };
    case "loading-portfolio":
      return { state: portfolioState };
    case "ready":
      return {
        state: portfolioState,
        actions: {
          notConnected: notConnectedCompaniesActions(dispatch, portfolioState),
          addCompanyToPortfolioActions: addCompanyToPortfolioActions(
            dispatch,
            portfolioState,
            store,
            locale,
          ),
          companySearchActions: searchActions(dispatch, portfolioState, store, locale),
          newProjectActions: newProjectActions(dispatch, portfolioState, i18n),
          portfolioActions: portfolioInstanceActions(
            portfolioState,
            dispatch,
            store,
            isSystemPortfolio,
            locale,
            setCustomRoleId,
          ),
          signShareholderRegistersActions: signShareholderRegistersActions(
            dispatch,
            portfolioState,
            i18n,
          ),
          openPortfolioSettings: () => dispatch(togglePortfolioSettings(portfolioState, true)),
          closePortfolioSettings: () => dispatch(togglePortfolioSettings(portfolioState, false)),
          savePortfolioSettings: () =>
            dispatch(savePortfolioSettings(portfolioState, onUpdatePortfolio)),
          confirmDissolvePortfolio: () =>
            dispatch(toggleConfirmDissolvePortfolio(portfolioState, true)),
          closeConfirmDissolvePortfolio: () =>
            dispatch(toggleConfirmDissolvePortfolio(portfolioState, false)),
          dissolvePortfolio: () => dispatch(dissolvePortfolio(portfolioState, onRemovePortfolio)),
          closePendingInvitationDialog: () =>
            dispatch([updateStore(portfolioState, { openInvitationRequest: undefined })]),
          openInviteAdministratorDialog: () =>
            dispatch(openInviteAdminDialog(locale, portfolioState.portfolio)),
          openExportShareholdersDialog: () =>
            dispatch(openExportShareholdersDialog(portfolioState.portfolioResponse, dispatch)),
          openTransferOwnershipDialog: () => dispatch(openTransferOwnershipDialog(portfolioState)),
          closeTransferOwnershipDialog: () =>
            dispatch([updateStore(portfolioState, { showTransferOwnershipDialog: false })]),
          sendOwnershipTransfer: () => dispatch(sendOwnershipTransfer(portfolioState)),
          openCreateProjectDialog: (decisionType) =>
            dispatch(openCreateProjectDialog({ portfolioState, decisionType })),
          setCompaniesWithSubscriptionStatus: (status) =>
            dispatch(setCompaniesWithSubscriptionStatus(portfolioState, status)),
          openCreatePortfolioDialog: () => dispatch([showCreatePortfolioDialog]),
          getCustomRoleItems: () =>
            dispatch(
              fetchCustomRoleItems(
                portfolioState.portfolio,
                portfolioState,
                portfolioState.portfolioResponse.portfolio?.id,
              ),
            ),
          openInviteCustomRoleDialog: (customRoleItem) =>
            dispatch(
              openInviteCustomRoleDialog({
                state: portfolioState,
                dispatch,
                locale,
                apiService,
                customRoleItem,
              }),
            ),
          resetCustomRoleFilter: () => dispatch(onResetCustomRoleFilter(portfolioState, locale)),
        },
      };
  }
};

const sendOwnershipTransfer = (portfolioState: ReadyState): DCEvent[] => {
  const selectedOwner = portfolioState.transferOwnershipModel?.value;
  if (!selectedOwner?.id) return [];
  return [
    updateStore(portfolioState, {
      showTransferOwnershipDialog: true,
      isLoadingOwnershipTransfer: true,
    }),
    portfolioApi.updatePortfolioOwnership({
      portfolioId: portfolioState.portfolioResponse.portfolio?.id,
      externalId: selectedOwner.id,
      onSuccess: (res) => [
        updateStore(portfolioState, {
          showTransferOwnershipDialog: false,
          isLoadingOwnershipTransfer: false,
          portfolioResponse: res.data,
        }),
        showGeneralSuccess(),
      ],
      onFailure: () => [
        updateStore(portfolioState, {
          showTransferOwnershipDialog: false,
          isLoadingOwnershipTransfer: false,
        }),
        showGeneralError(),
      ],
    }),
  ];
};

const onResetCustomRoleFilter = (state: ReadyState, locale: Language): DCEvent[] => {
  const roleItem = uniqBy(
    state.userCompaniesInPortfolio
      .map((company) => company.role)
      .filter((role) => role !== undefined),
    "customRoleId",
  );
  return [
    updateStore(state, {
      userCompaniesInPortfolio: state.userCompaniesInPortfolio.filter((a) => a.isCustomer),
      customRoleFilter: initCustomRoleFilter(roleItem, locale),
    }),
  ];
};

export const openInviteCustomRoleDialog = ({
  state,
  dispatch,
  locale,
  apiService,
  customRoleItem,
}: {
  state: ReadyState;
  dispatch: PortfolioPageDispatcher;
  locale: Language;
  apiService: ApiService;
  customRoleItem: CustomRoleItem;
}): DCEvent[] => {
  const creatorCompany = state.userCompaniesInPortfolio
    .filter((allCompanies) => allCompanies.accessType === AccessType.ADMIN)
    .find((adminCompany) => adminCompany.organisationNumber === customRoleItem.role.orgNo);
  return [
    {
      type: "show-dialog",
      dialog: {
        type: "invite-custom-role-user",
        model: new InviteUserToCustomRoleModel({
          customRoleItem,
          availableCompanies: toSelectableCompanies(
            state.customRoleItem.availableCompanies,
            state.portfolioResponse.userCompanies,
          ),
          apiService,
          locale,
          dispatch,
          isLoadingInvite: false,
          showEmailField: true,
          hasAdminInCreatorCompany: creatorCompany !== undefined,
          selectedUserCompanyItems: [],
          userName: "",
          portfolioName: state.portfolio.label,
          selectedPortfolioId:
            state.portfolio.type === "portfolio-instance"
              ? state.portfolio.portfolio.id
              : undefined,
          closeDialog: () =>
            dispatch([
              {
                type: "closeDialog",
              },
            ]),
        }),
      },
    },
  ];
};

const fetchCustomRoleItems = (
  portfolio: SelectedPortfolioOption,
  state: ReadyState,
  portfolioId?: string,
): DCEvent[] => {
  return [
    {
      type: "update-store",
      data: {
        portfolioState: {
          ...state,
          isLoadingCustomRole: true,
        },
      },
    },
    portfolioApi.getCustomRoleItems({
      portfolio,
      portfolioId,
      onSuccess: (res) => {
        return [
          {
            type: "update-store",
            data: {
              portfolioState: {
                ...state,
                isLoadingCustomRole: false,
                customRoleItem: res.data,
              },
            },
          },
        ];
      },
      onFailure: () => [
        showError({ key: "general-error" }, { autoHide: true }),
        updateStore(state, {
          ...state,
          isLoadingCustomRole: false,
          customRoleItem: {
            customRoleItems: [],
            availableCompanies: [],
          },
        }),
      ],
    }),
  ];
};

export const setCompaniesWithSubscriptionStatus = (
  state: ReadyState,
  status: ActiveSubscribedCompanies,
): DCEvent[] => {
  return [
    updateStore(state, {
      ...state,
      companySubscription: status,
    }),
  ];
};

export const openCreateProjectDialog = ({
  portfolioState,
  decisionType,
}: {
  portfolioState: ReadyState;
  decisionType: DecisionType;
}): DCEvent[] => {
  const adminCompanies = toCompaniesWithDbotAccess(portfolioState).filter(
    (c) => c.accessType === AccessType.ADMIN,
  );
  if (adminCompanies.length !== 1) {
    return openCreateBatchProjectDialog(portfolioState, decisionType);
  } else {
    return openDBotProjectList(portfolioState);
  }
};

export const openCreateBatchProjectDialog = (
  portfolioState: ReadyState,
  decisionType: DecisionType,
): DCEvent[] => {
  const selectedCompanies = toCompaniesWithDbotAccess(portfolioState).map(
    (f) => f.organisationNumber,
  );
  const index = portfolioState.checkedDecisions.indexOf(decisionType);
  const checkedDecisions =
    index > -1
      ? portfolioState.checkedDecisions.filter((d) => d !== decisionType)
      : portfolioState.checkedDecisions.concat(decisionType);
  return [
    updateStore(portfolioState, {
      isNewProjectDialogOpen: true,
      selectedCompanies,
      checkedDecisions,
      companySearchFieldModel: new StringFieldModel(),
    }),
  ];
};

export const leavePortfolioCompaniesPage: UpdateStoreEvent[] = [
  {
    type: "update-store",
    data: {
      portfolioState: (state?: PortfolioPageState) =>
        state?.type === "ready" ? { type: "loading-portfolio", portfolio: state.portfolio } : state,
    },
  },
];

export const initPortfolioCompaniesPage = ({
  dispatch,
  currentUrl,
  portfolio,
  activeHeader,
  companiesTab,
  companiesMode,
  signDocumentCounts,
  store,
  force,
  language,
}: InitPortfolioCompaniesPageProps): DCEvent[] => {
  const header = mapToHeader(activeHeader);
  const activeCompaniesTab: PortfolioCompaniesTab = companiesTab ?? "all";
  const portfolioState = store.portfolioState;

  // keep state when switch tabs
  if (
    companiesTab &&
    !force &&
    portfolioState?.type === "ready" &&
    isSamePortfolio(portfolio, portfolioState.portfolio)
  ) {
    if (portfolioState.companiesTab !== activeCompaniesTab) {
      return [
        updateStore(portfolioState, {
          companiesTab: activeCompaniesTab,
          userCompaniesInPortfolio: extractUserCompaniesInPortfolio(
            portfolioState.portfolioResponse,
            activeCompaniesTab,
          ),
          companiesTabs: createCompaniesTabs(
            portfolioState.portfolioResponse,
            dispatch,
            extractUserCompaniesInPortfolio(portfolioState.portfolioResponse, "all"),
            activeCompaniesTab,
            portfolio,
          ),
          quickFilters: createPortfolioQuickFilters(portfolioState.portfolioResponse, portfolio),
        }),
      ];
    }
    return [];
  }

  return [
    {
      type: "update-store",
      data: {
        portfolioState: { type: "loading-portfolio", portfolio, activeHeader: "companies" },
      },
    },
    portfolioApi.getPortfolio({
      portfolio,
      onSuccess: (res) => {
        const allCompanies = companiesInPortfolio(res.data);
        const notConnected = mapToNotConnectedState(res.data, allCompanies, "all");
        const userCompaniesInPortfolio = extractUserCompaniesInPortfolio(
          res.data,
          activeCompaniesTab,
        );
        const alreadyInPortfolio = companiesInPortfolio(res.data);
        const totalSignDocumentCount = res.data.signDocumentCount;
        const roles: CustomRole[] | undefined = uniqBy(
          userCompaniesInPortfolio
            .map((company) => company.role)
            .filter((role) => role !== undefined),
          "customRoleId",
        );
        return [
          {
            type: "update-store",
            data: {
              followUpCounts: toFollowUpCounts(res.data),
            },
          },
          {
            type: "update-store",
            data: {
              portfolioState: {
                type: "ready",
                signDocumentCounts,
                portfolio,
                companiesTab: activeCompaniesTab,
                portfolioSearchModel: new StringFieldModel(),
                isAddCompanyDialogOpen: false,
                portfolioResponse: res.data,
                selectedCompanies: [],
                checkedDecisions: [],
                companiesMode:
                  companiesMode ?? createCompaniesModeRadioFieldModel(res.data.companyItems),
                availableCompanies: res.data.userCompanies.filter(
                  (c) => !alreadyInPortfolio.includes(c),
                ),
                userCompaniesInPortfolio,
                companiesTabs: createCompaniesTabs(
                  res.data,
                  dispatch,
                  allCompanies,
                  activeCompaniesTab,
                  portfolio,
                ),
                quickFilters: createPortfolioQuickFilters(res.data, portfolio),
                activeHeader: header,
                currentUrl,
                totalSignDocumentCount,
                portfolioSettingsModel: {
                  name: new StringFieldModel({ value: res.data.portfolio?.name, required: true }),
                },
                notConnected,
                portfolioCreationInfo: portfolioCreationInfo(res.data.portfolio),
                companySubscription: "all",
                adminsCompanyCount: res.data.userCompanies.filter(
                  (allCompanies) => allCompanies.accessType === AccessType.ADMIN,
                ).length,
                isLoadingCustomRole: false,
                customRoleItem: {
                  customRoleItems: [],
                  availableCompanies: [],
                },
                customRoleFilter:
                  roles && roles.length >= 1 ? initCustomRoleFilter(roles, language) : undefined,
                arStatusFilter:
                  new RadioFieldModel<ShareholderRegisterStatementPortfolioStatusType | null>({
                    options: [null, "Approved", "Rejected", "Sent", "NotSent"],
                    value: null,
                    idFn: identity,
                    labelFn: (value) => ({
                      key: value
                        ? shareholderRegisterStatementPortfolioStatusTypeLabels[value]
                        : "status-all",
                    }),
                  }),
                userSettings: res.data.userSettings,
              },
            },
          },
        ];
      },
      onFailure: () => [
        {
          type: "update-store",
          data: { portfolioState: { type: "invalid-portfolioid", portfolio } },
        },
      ],
    }),
  ];
};

export function initCustomRoleFilter(roleItem: CustomRole[], language: Language) {
  const items = orderBy(roleItem, "desc");
  return new RadioFieldModel<CustomRole | null>({
    value: null,
    options: [null, ...items],
    idFn: (option) => (option ? option.customRoleId : "all"),
    labelFn: (option) =>
      option ? { translated: getRoleNameByLanguage(option, language) } : { key: "all-roles" },
    iconFn: (option) => (option ? "user-square" : "list-bullets"),
  });
}

export function filteredUserCompanies(state: ReadyState) {
  const isActive = state.companySubscription === "active";
  const activeCompanies = new Set(
    state.portfolioResponse.activeSubscriptionCompanies?.map(
      (subscribed) => subscribed.organisationNumber,
    ),
  );
  const filteredCompanies =
    state.companySubscription === "all" &&
    (state.customRoleFilter === undefined || state.customRoleFilter.value === null)
      ? state.userCompaniesInPortfolio
      : state.customRoleFilter !== undefined && state.customRoleFilter.value !== null
        ? state.userCompaniesInPortfolio.filter(
            (c) => c.role?.customRoleId === state.customRoleFilter?.value?.customRoleId,
          )
        : state.userCompaniesInPortfolio.filter((company) => {
            const isSubscribed = activeCompanies.has(company.organisationNumber);
            return isActive ? isSubscribed : !isSubscribed;
          });
  const companies =
    state.companiesTab === "ar-status" && state.arStatusFilter?.value
      ? filteredCompanies.filter(
          (c) => c.shareholderRegisterStatementStatus?.type === state.arStatusFilter?.value,
        )
      : filteredCompanies;
  const query = state.portfolioSearchModel.inputValue.toLowerCase();
  return companies.filter(
    (c) =>
      c.name.toLowerCase().includes(query) || c.organisationNumber.toLowerCase().includes(query),
  );
}

export function isNoActiveSubscription(state: ReadyState, orgNo: string) {
  const activeCompanies = new Set(
    state.portfolioResponse.activeSubscriptionCompanies?.map(
      (subscribed) => subscribed.organisationNumber,
    ),
  );
  return !activeCompanies.has(orgNo);
}

export const mapToNotConnectedState = (
  data: PortfolioResponse,
  all: PortfolioCompany[],
  roleFilter: RoleFilter = "all",
): NotConnectedTabState => ({
  ...defaultNotConnectedState,
  companies: toPortfolioCompanies(data.incompletedInvitationRequests)
    .concat(all.filter((c) => !c.isCustomer))
    .concat(subsidiariesToPortfolioCompany(data.subsidiaries, data.companyItems))
    .filter(
      (c) =>
        roleFilter === "all" ||
        (roleFilter === "chairperson" && c.isChairperson) ||
        (roleFilter === "ceo" && c.isCEO) ||
        (roleFilter === "member" && c.isBoardmember) ||
        (roleFilter === "shareholder" && c.isShareholder),
    ),
});

export type AddCompanyToPortfolioActions = {
  openAddCompanyDialog: () => void;
  closeAddCompanyDialog: () => void;
  saveCompaniesInPortfolio: () => void;
  toggleCompanyInAddDialog: (orgNo: string) => void;
  isCompanySelected: (orgNo: string) => boolean;
  toggleAllCompanies: () => void;
  deselectAllCompanies: () => void;
  filterSelectedCompanies?: () => void;
  setPrimaryCompany: (orgNo: string) => void;
};

export type CompanySearchActions = {
  openNorwegianCompanySearch: () => void;
  openSwedishCompanySearch: () => void;
  closeNorwegianCompanySearch: () => void;
  closeSwedishCompanySearch: () => void;
  selectCompany: (company: SuggestedCompany) => void;
  selectSwedishCompany: (company: SuggestedCompany) => void;
  searchForCompany: (query: string) => void;
  sendAskForPermissionRequest: (autoonboard: boolean) => void;
  searchSwedishBusinessRegister: () => void;
};

export type NewProjectActions = {
  openNewProjectDialog: () => void;
  closeNewProjectDialog: () => void;
  toggleDecision: (decisionType: DecisionType) => void;
  checkedDecisions: DecisionType[];
  companiesMode: RadioFieldModel<NewProjectCompaniesMode>;
  createBatchProject: () => void;
};

export type SignShareholderRegistersActions = {
  openDialog: () => void;
  closeDialog: () => void;
  createProject: (form: SignShareholderRegistersForm) => void;
};

export type NewProjectCompaniesMode = "portfolio" | "selected_companies";

export type ActionsForNotConnectedCompanies = {
  toggleConnectAllDialog: () => void;
  toggleCheckedSubsidiary: (subsidiary: ConnectSubsidiaryItemState) => void;
  submitConnectAll: (autoonboard: boolean) => void;
  selectAll: () => void;
  selectNone: () => void;
  filterWithRole: (role: RoleFilter) => void;
};

export type PortfolioInstanceActions = {
  onAddAdministrator: ({ companyItem, lang }: { companyItem: CompanyItem; lang: Language }) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  onNewMeeting: (organisationNumber: string, meetingType: MeetingType) => void;
  onSelect: (company: PortfolioCompany) => void;
  filterWithText: () => void;
  onRemoveFromPortfolio?: (company: PortfolioCompany) => void;
  closeRemoveCompanyDialog: () => void;
  removeFromPortfolio: () => void;
  onMoveToPortfolio?: (company: PortfolioCompany) => void;
  closeMoveCompanyToPortfolioDialog: () => void;
  setDestinationPortfolio: (portfolio: Portfolio) => void;
  moveCompanyToPortfolio: () => void;
  openDBotProjectList: () => void;
  openBeneficialOwnersPage: (company: PortfolioCompany) => void;
  openARPage: (company: PortfolioCompany) => void;
  copyToClipboard: (text: string) => void;
  navigateToLastTransaction: (orgNo: string, transactionId: number) => void;
  onHoverLastTransaction: (transactionLogId: number) => void;
};

export type PortfolioPageActions = {
  notConnected: ActionsForNotConnectedCompanies;
  addCompanyToPortfolioActions: AddCompanyToPortfolioActions;
  companySearchActions: CompanySearchActions;
  newProjectActions: NewProjectActions;
  portfolioActions: PortfolioInstanceActions;
  closePendingInvitationDialog: () => void;
  openInviteAdministratorDialog: () => void;
  openExportShareholdersDialog: () => void;
  signShareholderRegistersActions: SignShareholderRegistersActions;
  openTransferOwnershipDialog: () => void;
  closeTransferOwnershipDialog: () => void;
  sendOwnershipTransfer: () => void;
  openCreateProjectDialog: (decisionType: DecisionType) => void;
  setCompaniesWithSubscriptionStatus: (status: ActiveSubscribedCompanies) => void;
  openCreatePortfolioDialog: () => void;
  getCustomRoleItems: () => void;
  openInviteCustomRoleDialog: (customRoleItem: CustomRoleItem) => void;
  resetCustomRoleFilter: () => void;
} & PortfolioSettingsActions;

export function createCompaniesModeRadioFieldModel(companies: CompanyItem[]) {
  return new RadioFieldModel<NewProjectCompaniesMode>({
    value: "portfolio",
    options: ["portfolio", "selected_companies"],
    idFn: identity,
    labelFn: (l) =>
      l === "portfolio"
        ? {
            key: "portfolio.newProject.entire-portfolio",
            params: { n: companies.length },
          }
        : { key: "portfolio.batch-project.select-companies" },
  });
}

const notConnectedCompaniesActions = (
  dispatch: PortfolioPageDispatcher,
  state: ReadyState,
): ActionsForNotConnectedCompanies => {
  return {
    toggleConnectAllDialog: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          showConnectAllDialog: !state.notConnected.showConnectAllDialog,
          subsidiaries: mapToConnectState(state),
        }),
      ]),
    toggleCheckedSubsidiary: (s) => {
      const subsidiaries = state.notConnected.subsidiaries;
      subsidiaries[subsidiaries.indexOf(s)] = { ...s, checked: !s.checked };
      dispatch([updateNotConnectedTabState(state, { subsidiaries })]);
    },
    submitConnectAll: (autoonboard: boolean) => dispatch(submitConnectAll(state, autoonboard)),
    selectAll: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          subsidiaries: state.notConnected.subsidiaries.map((s) => ({ ...s, checked: true })),
        }),
      ]),
    selectNone: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          subsidiaries: state.notConnected.subsidiaries.map((s) => ({ ...s, checked: false })),
        }),
      ]),
    filterWithRole: (value) => {
      state.notConnected.rolesFilter.setValue(value);
      dispatch([
        updateNotConnectedTabState(state, {
          companies: mapToNotConnectedState(
            state.portfolioResponse,
            state.userCompaniesInPortfolio,
            value,
          ).companies,
        }),
      ]);
    },
  };
};
export type RoleFilter = "all" | "ceo" | "chairperson" | "member" | "shareholder";
const roleFilterLabels: { [key in RoleFilter]: TMessageKey } = {
  ["all"]: "roles.all",
  ["ceo"]: "roles.ceo",
  ["chairperson"]: "roles.chairperson",
  ["member"]: "roles.member",
  ["shareholder"]: "portfolio.SHAREHOLDER",
};

export const defaultNotConnectedState: NotConnectedTabState = {
  showConnectAllDialog: false,
  subsidiaries: [],
  companies: [],
  rolesFilter: new RadioFieldModel<RoleFilter>({
    value: "all",
    options: ["all", "ceo", "chairperson", "member", "shareholder"],
    idFn: identity,
    labelFn: (l) => ({ key: roleFilterLabels[l] }),
  }),
};

const mapToConnectState = (state: ReadyState): ConnectSubsidiaryItemState[] =>
  state.notConnected.showConnectAllDialog
    ? state.notConnected.subsidiaries
    : state.portfolioResponse.subsidiaries
        .filter((s) =>
          state.notConnected.companies.some((c) => c.organisationNumber === s.organizationNumber),
        )
        .map<ConnectSubsidiaryItemState>((subsidiary) => ({
          checked: true,
          receiverEmail: new StringFieldModel({ required: true, validator: emailValidator }),
          subsidiary,
        }));

const updateNotConnectedTabState = (
  state: ReadyState,
  update: Partial<NotConnectedTabState>,
): DCEvent =>
  updateStore(state, {
    notConnected: {
      ...state.notConnected,
      ...update,
    },
  });

const submitConnectAll = (state: ReadyState, autoonboard: boolean): DCEvent[] => {
  state.notConnected.subsidiaries.forEach((s) => {
    s.receiverEmail.setRequired(s.checked && !autoonboard);
    s.receiverEmail.validate();
  });
  if (state.notConnected.subsidiaries.every((s) => s.receiverEmail.isValid)) {
    const data: ConnectCompaniesRequest = {
      autoonboard,
      portfolioId: state.portfolioResponse.portfolio?.id,
      connectData: state.notConnected.subsidiaries
        .filter((s) => s.checked)
        .map((s) => ({
          company: {
            companyName: s.subsidiary.name,
            organisationNumber: s.subsidiary.organizationNumber,
          },
          receiverEmail: s.receiverEmail.validatedValue,
        })),
    };
    const onSuccess = (): DCEvent[] => [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: undefined,
        showConnectAllDialog: false,
      }),
      showAlert({ key: "general-success" }),
    ];

    const onFailure = (e: unknown): DCEvent[] => [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: failure(),
      }),
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: undefined,
        showConnectAllDialog: false,
      }),
      showError({
        key: isHttpError(HttpStatus.NOT_FOUND, e) ? "general-missing-access" : "general-error",
      }),
    ];

    return [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: pending(),
      }),
      portfolioApi.connectAllCompanies({ data, onSuccess, onFailure }),
    ];
  } else {
    return [];
  }
};

export type ConnectCompaniesRequest = {
  connectData: ConnectData[];
  autoonboard: boolean;
  portfolioId?: string;
};

export type ConnectData = {
  company: CompanyItem;
  receiverEmail: string;
};

export type AcceptUserTermsAndUpdateEmailRequest = {
  email: string;
};

const addCompanyToPortfolioActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  store: PortfolioPageStore,
  language: Language,
): AddCompanyToPortfolioActions => ({
  openAddCompanyDialog: () => dispatch(openCompanyDialog(portfolioState)),
  closeAddCompanyDialog: () => dispatch(closeCompanyDialog(portfolioState)),
  toggleCompanyInAddDialog: (orgNo) => dispatch(toggleCompanyInAddDialog(portfolioState, orgNo)),
  isCompanySelected: (orgNo) => portfolioState.selectedCompanies.indexOf(orgNo) > -1,
  saveCompaniesInPortfolio: () =>
    dispatch(
      addCompaniesToPortfolio(
        portfolioState,
        initPortfolioCompaniesPage({
          dispatch,
          currentUrl: portfolioState.currentUrl,
          portfolio: portfolioState.portfolio,
          activeHeader: portfolioState.activeHeader,
          companiesTab: portfolioState.companiesTab,
          companiesMode: portfolioState.companiesMode,
          signDocumentCounts: portfolioState.signDocumentCounts,
          store,
          force: true,
          language,
        }),
      ),
    ),
  toggleAllCompanies: () => {
    const orgNos = toCompaniesWithDbotAccess(portfolioState)
      .filter((c) => c.accessType === AccessType.ADMIN)
      .map((c) => c.organisationNumber);
    dispatch([updateStore(portfolioState, { selectedCompanies: orgNos })]);
  },
  deselectAllCompanies: () =>
    dispatch([updateStore(portfolioState, { selectedCompanies: [], showOnlySelected: false })]),
  filterSelectedCompanies:
    portfolioState.selectedCompanies.length >= 1
      ? () =>
          dispatch([
            updateStore(portfolioState, { showOnlySelected: !portfolioState.showOnlySelected }),
          ])
      : undefined,
  setPrimaryCompany: (orgNo) =>
    dispatch([updateStore(portfolioState, { primaryCompanyOrgNo: orgNo })]),
});

const searchActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  store: PortfolioPageStore,
  language: Language,
): CompanySearchActions => ({
  openNorwegianCompanySearch: () => dispatch(toggleNorwegianCompanySearch(portfolioState, true)),
  openSwedishCompanySearch: () => dispatch(toggleSwedishCompanySearch(portfolioState, true)),
  closeNorwegianCompanySearch: () => dispatch(toggleNorwegianCompanySearch(portfolioState, false)),
  closeSwedishCompanySearch: () => dispatch(toggleSwedishCompanySearch(portfolioState, false)),
  selectCompany: (company) => dispatch(selectCompanyFromSearchResult(portfolioState, company)),
  selectSwedishCompany: (company) => dispatch(selectSwedishCompany(portfolioState, company)),
  searchForCompany: (query) => {
    portfolioState.norwegianCompanySearchDialogState?.companyNameSearchField.setInputValue(query);
    if (query.length >= 2) {
      debouncedSearchForCompany(dispatch, portfolioState, query);
    }
  },
  sendAskForPermissionRequest: (autoonboard: boolean) =>
    dispatch(sendAskForPermissionRequest(portfolioState, autoonboard, store, dispatch, language)),
  searchSwedishBusinessRegister: () => dispatch(searchSwedishBusinessRegister(portfolioState)),
});

const debouncedSearchForCompany = debounce(
  (dispatch: (events: DCEvent[]) => void, portfolioState: ReadyState, query: string) => {
    dispatch(searchForCompany(portfolioState, query));
  },
  300,
);

const newProjectActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  i18n: PreppedI18nState,
): NewProjectActions => ({
  openNewProjectDialog: () => dispatch(newProjectDialog(portfolioState)),
  closeNewProjectDialog: () => dispatch(closeProjectDialog(portfolioState)),
  toggleDecision: (decisionType) => dispatch(toggleDecision(portfolioState, decisionType)),
  checkedDecisions: portfolioState.checkedDecisions,
  companiesMode: portfolioState.companiesMode,
  createBatchProject: () =>
    dispatch(
      createBatchProject(
        portfolioState,
        createProjectName({
          decisions: portfolioState.checkedDecisions.map((d) => ({ decisionType: d })),
          i18n,
        }),
      ),
    ),
});

const signShareholderRegistersActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  i18n: PreppedI18nState,
): SignShareholderRegistersActions => ({
  openDialog: () => dispatch(openSignShareholderRegistersDialog(portfolioState)),
  closeDialog: () => dispatch(closeSignShareholderRegistersDialog(portfolioState)),
  createProject: (form) =>
    dispatch(
      createShareholderRegisterSigningProject(
        portfolioState,
        form,
        i18n.t("portfolio.sign-shareholder-registers.project-name"),
      ),
    ),
});

const portfolioInstanceActions = (
  portfolioState: ReadyState,
  dispatch: (events: DCEvent[]) => void,
  store: PortfolioPageStore,
  isSystemPortfolio: boolean,
  language: Language,
  setCustomRoleId?: (roleId: string) => void,
): PortfolioInstanceActions => ({
  isRenderStar: (orgNo: string, accessType: AccessType) =>
    isStarred(orgNo, accessType, portfolioState),
  onClickStar: (orgNo: string, accessType: AccessType) =>
    dispatch(toggleStarred(portfolioState, orgNo, accessType)),
  onAddAdministrator: ({ companyItem, lang }: { companyItem: CompanyItem; lang: Language }) =>
    dispatch(openSendAdministratorInvitationDialog(companyItem, lang)),
  onNewMeeting: (orgNo, meetingType) => dispatch([{ type: "new-meeting", orgNo, meetingType }]),
  onSelect: (company: PortfolioCompany) =>
    dispatch(selectCompany(portfolioState, dispatch, store, company, language, setCustomRoleId)),
  filterWithText: () => dispatch(filterPortfolioWithText(portfolioState)),
  onRemoveFromPortfolio: isSystemPortfolio
    ? undefined
    : (company: PortfolioCompany) =>
        dispatch(openRemoveFromPortfolioDialog(portfolioState, company)),
  closeRemoveCompanyDialog: () => dispatch(closeRemoveFromPortfolioDialog(portfolioState)),
  removeFromPortfolio: () => {
    dispatch(
      removeFromPortfolio(
        portfolioState,
        initPortfolioCompaniesPage({
          dispatch,
          currentUrl: portfolioState.currentUrl,
          portfolio: portfolioState.portfolio,
          activeHeader: portfolioState.activeHeader,
          companiesTab: portfolioState.companiesTab,
          companiesMode: portfolioState.companiesMode,
          signDocumentCounts: portfolioState.signDocumentCounts,
          store,
          force: true,
          language,
        }),
      ),
    );
  },
  onMoveToPortfolio: isSystemPortfolio
    ? undefined
    : (company: PortfolioCompany) => dispatch(openMoveToPortfolioDialog(portfolioState, company)),
  closeMoveCompanyToPortfolioDialog: () => dispatch(closeMoveToPortfolioDialog(portfolioState)),
  setDestinationPortfolio: (portfolio: Portfolio) =>
    dispatch(setDestinationPortfolio(portfolioState, portfolio)),
  moveCompanyToPortfolio: () => dispatch(moveCompanyToPortfolio(portfolioState)),
  openDBotProjectList: () => dispatch(openDBotProjectList(portfolioState)),
  openBeneficialOwnersPage: (company: PortfolioCompany) =>
    dispatch([navigate(adminBeneficialOwners(company.organisationNumber))]),
  openARPage: (company: PortfolioCompany) =>
    dispatch([navigate(adminShareholderStatement(company.organisationNumber))]),
  copyToClipboard: (text) => dispatch([copyToClipboard(text)]),
  navigateToLastTransaction: (orgNo, transactionId) =>
    dispatch([navigate(adminShareholdersTransaction(orgNo, transactionId), { newWindow: true })]),
  onHoverLastTransaction: (transactionLogId) =>
    dispatch(onHoverLastTransaction(portfolioState, transactionLogId)),
});

const onHoverLastTransaction = (state: ReadyState, transactionLogId: number): DCEvent[] => {
  return [
    updateStore(state, {
      hoveredTransaction: pending(),
    }),
    portfolioApi.getLastTransactionById({
      id: transactionLogId,
      onSuccess: (res) => [
        updateStore(state, {
          hoveredTransaction: ok(res.data),
        }),
      ],
      onFailure: () => [
        showGeneralError(),
        updateStore(state, {
          hoveredTransaction: failure(),
        }),
      ],
    }),
  ];
};

const openDBotProjectList = (state: ReadyState): DCEvent[] => {
  const organisationNumber = state.userCompaniesInPortfolio
    .filter((c) => c.accessType === AccessType.ADMIN)
    .find((a) =>
      state.portfolioResponse.companyItems.filter(
        (s) => s.organisationNumber === a.organisationNumber,
      ),
    )!.organisationNumber;
  return [navigate(adminProjects(organisationNumber))];
};

export const filterStarredInPortfolio = (
  userCompaniesInPortfolio: PortfolioCompany[],
  starred?: StarredCompany[],
): PortfolioCompany[] =>
  userCompaniesInPortfolio.filter(
    (c) =>
      starred?.some(
        (s) => s.organisationNumber === c.organisationNumber && s.accessType === c.accessType,
      ) ?? false,
  );

export type FilteredCompanyForDialog = {
  name: string;
  organisationNumber: string;
  isSelected: boolean;
  isPrimary: boolean;
};

export const filterCompaniesForAddDialog = (
  companies: PortfolioCompany[],
  searchTerm: string | undefined,
  action?: Pick<AddCompanyToPortfolioActions, "isCompanySelected">,
  primaryCompanyOrgNo?: string,
): FilteredCompanyForDialog[] => {
  const filtered = orderlessFilter(
    companies.filter((c) => c.accessType === AccessType.ADMIN),
    (c) => [c.name, c.organisationNumber],
    searchTerm,
  );
  const firstSelected = filtered.find((c) => action?.isCompanySelected(c.organisationNumber));
  const primaryOrgNo =
    primaryCompanyOrgNo && action?.isCompanySelected(primaryCompanyOrgNo)
      ? primaryCompanyOrgNo
      : firstSelected?.organisationNumber;
  return filtered.map(({ name, organisationNumber }) => ({
    name,
    organisationNumber,
    isSelected: action?.isCompanySelected(organisationNumber) ?? false,
    isPrimary: organisationNumber === primaryOrgNo,
  }));
};

export const filterNotConnected = (
  companies: PortfolioCompany[],
  roleFilter: RoleFilter,
): PortfolioCompany[] =>
  companies.filter((c) => {
    return (
      !c.isCustomer &&
      (roleFilter === "all" ||
        (roleFilter === "ceo" && c.isCEO) ||
        (roleFilter === "chairperson" && c.isChairperson) ||
        (roleFilter === "member" && c.isBoardmember) ||
        (roleFilter === "shareholder" && c.isShareholder))
    );
  });

export function createCompaniesTabs(
  response: PortfolioResponse,
  dispatch: (events: DCEvent[]) => void,
  allCompanies: PortfolioCompany[],
  companiesTab: PortfolioCompaniesTab,
  portfolio: SelectedPortfolioOption,
): TabButtonProps[] {
  const isAdmin = hasAdminAccess(response);
  return compact([
    {
      id: "all",
      label: {
        key: "portfolio.all-companies-with-count",
        params: { count: allCompanies.filter((c) => c.isCustomer).length },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("all", portfolio)]),
      isActive: companiesTab === "all",
    },
    {
      id: "starred",
      label: {
        key: "companyStatus.starred",
        params: {
          count: filterStarredInPortfolio(allCompanies, response.userSettings.starredCompanies)
            .length,
        },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("starred", portfolio)]),
      isActive: companiesTab === "starred",
    },
    {
      id: "not-connected",
      label: {
        key: "companyStatus.not-connected",
        params: {
          count: mapToNotConnectedState(response, allCompanies).companies.length,
        },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("not-connected", portfolio)]),
      isActive: companiesTab === "not-connected",
    },
    isAdmin && showArStatus(portfolio)
      ? {
          id: "ar-status",
          label: { key: "companyStatus.ar-status" },
          onSelect: () => dispatch([navigateToCompaniesTabAction("ar-status", portfolio)]),
          isActive: companiesTab === "ar-status",
        }
      : undefined,
    isAdmin && showBeneficialOwnersTab(portfolio)
      ? {
          id: "beneficial-owners",
          label: { key: "companyStatus.beneficial-owners" },
          onSelect: () => dispatch([navigateToCompaniesTabAction("beneficial-owners", portfolio)]),
          isActive: companiesTab === "beneficial-owners",
        }
      : undefined,
  ]);
}

type QuickFilterPortfolioOption = Exclude<SelectedPortfolioOption, PortfolioAllCompanies>;

const investmentAccessTypes = [AccessType.SHAREHOLDER, AccessType.ADDITIONAL_REPRESENTATIVE];

const matchesRole = (company: PortfolioCompany, type: PortfolioType): boolean =>
  type === "my-investments"
    ? investmentAccessTypes.includes(company.accessType)
    : mapRoleToPortfolioType(company.accessType) === type;

const companiesForOption = (
  response: PortfolioResponse,
  option: QuickFilterPortfolioOption,
): PortfolioCompany[] =>
  response.userCompanies.filter((c) =>
    option.type === "portfolio-instance"
      ? c.portfolioId === option.portfolio.id
      : matchesRole(c, option.type),
  );

const hasCompanies = (response: PortfolioResponse, option: QuickFilterPortfolioOption): boolean =>
  companiesForOption(response, option).length > 0;

const quickFilterPortfolioOptions = (
  response: PortfolioResponse,
  portfolio: SelectedPortfolioOption,
): QuickFilterPortfolioOption[] =>
  [
    portfolioWithAdminRole,
    portfolioWithBoardRole,
    portfolioMyInvestments,
    ...response.portfolios.map(portfolioInstance),
  ].filter((option) => isSamePortfolio(option, portfolio) || hasCompanies(response, option));

export function createPortfolioQuickFilters(
  response: PortfolioResponse,
  portfolio: SelectedPortfolioOption,
): QuickFilter[] {
  const options = quickFilterPortfolioOptions(response, portfolio);
  if (options.length < 2) {
    return [];
  }
  return options.map((option) => ({
    id: option.type === "portfolio-instance" ? option.portfolio.id : option.type,
    label: option.label,
    isActive: isSamePortfolio(option, portfolio),
    onSelect: [
      navigateToPortfolioCompaniesAction(option),
      ...updateUserPrefEvent("portfolioQuickFiltersExpanded", true),
    ],
    tooltip: quickFilterTooltip(option, response),
  }));
}

const tooltipKey = (option: QuickFilterPortfolioOption): TMessageKey => {
  switch (option.type) {
    case "portfolio-instance":
      return "portfolio.quick-filters.tooltips.instance";
    case "with-admin-role":
      return "portfolio.quick-filters.tooltips.admin";
    case "with-board-role":
      return "portfolio.quick-filters.tooltips.board";
    case "my-investments":
      return "portfolio.quick-filters.tooltips.investments";
  }
};

export function quickFilterTooltip(
  option: QuickFilterPortfolioOption,
  response: PortfolioResponse,
): TMessage {
  const companies = uniqBy(companiesForOption(response, option), (c) => c.organisationNumber);
  return {
    key: tooltipKey(option),
    params: {
      count: companies.length,
      companyName: companies.length === 1 ? companies[0].name : undefined,
      portfolioName: option.type === "portfolio-instance" ? option.portfolio.name : undefined,
    },
  };
}

export function customRoleQuickFilters(
  customRoleFilter: RadioFieldModel<CustomRole | null> | undefined,
  companiesInPortfolio: PortfolioCompany[],
  language: Language,
): QuickFilter[] {
  if (!customRoleFilter) {
    return [];
  }
  return customRoleFilter.options
    .filter((option) => option !== null)
    .map((role) => {
      const companies = companiesInPortfolio.filter(
        (c) => c.role?.customRoleId === role.customRoleId,
      );
      const isActive = customRoleFilter.value?.customRoleId === role.customRoleId;
      const roleName = getRoleNameByLanguage(role, language);
      return {
        id: role.customRoleId,
        label: { translated: roleName },
        icon: "user-square",
        isActive,
        onSelect: () => customRoleFilter.setValue(isActive ? null : role),
        tooltip: {
          key: "portfolio.quick-filters.tooltips.custom-role",
          params: {
            count: companies.length,
            companyName: companies.length === 1 ? companies[0].name : undefined,
            roleName,
          },
        },
      };
    });
}

export function prepPortfolioQuickSelector({
  filters,
  customRoleFilter,
  companiesInPortfolio,
  userPrefs,
  language,
}: {
  filters: QuickFilter[];
  customRoleFilter: RadioFieldModel<CustomRole | null> | undefined;
  companiesInPortfolio: PortfolioCompany[];
  userPrefs: UserPrefs;
  language: Language;
}): QuickFilterBarProps {
  const isExpanded = userPrefs.portfolioQuickFiltersExpanded ?? false;
  return {
    filters: [
      ...filters,
      ...customRoleQuickFilters(customRoleFilter, companiesInPortfolio, language),
    ],
    isExpanded,
    onToggle: updateUserPrefEvent("portfolioQuickFiltersExpanded", !isExpanded),
    tooltipMessage: { key: "portfolio.quick-filters.more" },
    collapseFiltersLabel: { key: "portfolio.quick-filters.show" },
    expandFiltersLabel: { key: "portfolio.quick-filters.hide" },
    extraAction: {
      label: { key: "portfolio.new" },
      icon: { name: "plus", size: "sm" },
      tooltip: { key: "portfolio.new" },
      onSelect: [showCreatePortfolioDialog],
    },
  };
}

export function hasAdminAccess(response: PortfolioResponse) {
  return response.userCompanies.some((c) => c.accessType === AccessType.ADMIN);
}

export function isPortfolioOwner(response: PortfolioResponse) {
  return response.user.externalId === response.portfolio?.ownerId;
}

function showArStatus(portfolio: SelectedPortfolioOption) {
  return portfolio.type !== "with-board-role" && portfolio.type !== "my-investments";
}

function showBeneficialOwnersTab(portfolio: SelectedPortfolioOption) {
  return portfolio.type !== "with-board-role" && portfolio.type !== "my-investments";
}

export const isStarred = (orgNo: string, accessType: AccessType, state: ReadyState): boolean =>
  state.portfolioResponse.userSettings.starredCompanies?.some(
    (c) =>
      c.organisationNumber === orgNo &&
      c.accessType === accessType &&
      state.userCompaniesInPortfolio.some(
        (p) => p.organisationNumber === orgNo && p.accessType === accessType,
      ),
  ) ?? false;

export const toggleStarred = (
  state: ReadyState,
  orgNo: string,
  accessType: AccessType,
): DCEvent[] => {
  const isCurrentlyStarred = isStarred(orgNo, accessType, state);
  const httpRequest = isCurrentlyStarred ? portfolioApi.unstarCompany : portfolioApi.starCompany;
  const filteredCompanies = filterStarredInPortfolio(
    state.userCompaniesInPortfolio,
    state.portfolioResponse.userSettings.starredCompanies,
  );

  const starredCompanies = isCurrentlyStarred
    ? (filteredCompanies.filter(
        (c) => c.organisationNumber !== orgNo || c.accessType !== accessType,
      ) ?? [])
    : [...filteredCompanies, { organisationNumber: orgNo, accessType }];

  const userCompaniesInPortfolio =
    state.companiesTab === "starred"
      ? state.userCompaniesInPortfolio.filter((c) =>
          starredCompanies.some(
            (s) => s.organisationNumber === c.organisationNumber && s.accessType === c.accessType,
          ),
        )
      : state.userCompaniesInPortfolio;

  const onSuccess = (): DCEvent[] => [
    updateStore(state, {
      userCompaniesInPortfolio,
      portfolioResponse: {
        ...state.portfolioResponse,
        userSettings: {
          ...state.portfolioResponse.userSettings,
          starredCompanies,
        },
      },
      companiesTabs: state.companiesTabs.map((t) => ({
        ...t,
        label:
          t.id === "starred"
            ? { key: "companyStatus.starred", params: { count: starredCompanies.length } }
            : t.id === "not-connected"
              ? {
                  key: "companyStatus.not-connected",
                  params: { count: state.notConnected.companies.length },
                }
              : t.label,
      })),
    }),
  ];
  const onFailure = () => [];
  return [
    httpRequest({
      orgNo,
      accessType,
      onSuccess,
      onFailure,
    }),
  ];
};

export function toPortfolioCompanies(data: InvitationResponse[]): PortfolioCompany[] {
  const seenOrganisationNumbers = new Set<string>();

  return data.reduce<PortfolioCompany[]>((acc, it) => {
    if (!seenOrganisationNumbers.has(it.organisationNumber)) {
      seenOrganisationNumbers.add(it.organisationNumber);
      acc.push({
        id: it.id,
        organisationNumber: it.organisationNumber,
        name: it.companyName,
        isCustomer: false,
        accessType: AccessType.DRAFT,
        state: it.state as CompanyState,
        starred: false,
        currency: it.currency,
      });
    }
    return acc;
  }, []);
}

export const subscriptionStatus = ["all", "active", "inactive"] as const;
export type ActiveSubscribedCompanies = (typeof subscriptionStatus)[number];

export function activeCompanySubscriptionsLabel(status: ActiveSubscribedCompanies): TMessageKey {
  switch (status) {
    case "all":
      return "portfolio.companies-active-subscription-all";
    case "active":
      return "portfolio.companies-active-subscription-active";
    case "inactive":
      return "portfolio.companies-active-subscription-inactive";
  }
}

export const prepareCompaniesWithSubscriptionStatus = (
  value: ActiveSubscribedCompanies | undefined,
  onSelect: (value: ActiveSubscribedCompanies) => void,
): SelectProps<ActiveSubscribedCompanies> => ({
  value: value ?? "all",
  onSelect,
  items: subscriptionStatus.slice(),
  idFn: identity,
  labelFn: (option) => ({ key: activeCompanySubscriptionsLabel(option) }),
  iconFn: (option) => {
    switch (option) {
      case "active":
        return { name: "circle", color: "success", weight: "fill", size: "xs" };
      case "inactive":
        return { name: "circle", color: "subtle", size: "xs" };
      case "all":
        return "list-bullets";
    }
  },
});

export function companySearchDialogTitle(state: CompanySearchDialogState) {
  if (isOk(state.onboardingCandidatesResult) && state.onboardingCandidatesResult.data.isCustomer) {
    return "registerCompany.requestAccess.title";
  } else if (state.selectedCompany) {
    return "registerCompany.company.addCompany.header";
  } else {
    return "registerCompany.addCompany.header";
  }
}

export function filterSelectableCompanies(
  props: PortfolioCompaniesSelectProps,
): PortfolioCompany[] {
  const query = props.companySearchFieldModel.inputValue.toLowerCase();
  return props.companies
    .filter((c) => props.isContextCustomRole || c.accessType === AccessType.ADMIN)
    .filter(
      (c) =>
        !query ||
        c.name.toLowerCase().includes(query) ||
        c.organisationNumber.toLowerCase().includes(query) ||
        (props.isContextCustomRole && c.portfolioName?.toLowerCase().includes(query)),
    )
    .filter((c) => !props.showSelectedAll || props.action?.isCompanySelected(c.organisationNumber));
}

export function groupCompaniesByPortfolio(companies: PortfolioCompany[]): PortfolioCompanyGroup[] {
  const grouped = groupBy(companies, (c) => c.portfolioId ?? "");
  const groups = Object.entries(grouped).map(([portfolioId, group]) => ({
    portfolioId,
    portfolioName: group[0]?.portfolioName ?? "",
    companies: group,
  }));
  return orderBy(
    groups,
    [(g) => g.portfolioId === "", (g) => g.portfolioName.toLowerCase()],
    ["asc", "asc"],
  );
}

export function isDisabledOptionSelected(state: CompanySearchDialogState) {
  return (
    isOk(state.onboardingCandidatesResult) &&
    ((state.accessTypeModel?.value === "shareholder-portal" &&
      !state.onboardingCandidatesResult.data.isShareholderPortalEnabled) ||
      (state.accessTypeModel?.value === "board-portal" &&
        !state.onboardingCandidatesResult.data.isBoardPortalEnabled))
  );
}
